import React, { useState, useEffect, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
// FaTimes icon needed for the modal close button
import {
  FaUsers,
  FaSuitcase,
  FaSnowflake,
  FaCar,
  FaTaxi,
  FaRupeeSign,
  FaMapMarkerAlt,
  FaArrowRight,
  FaCheck,
  FaCheckCircle,
  FaCalendarAlt,
  FaClock,
  FaEdit,
  FaTimes,
  FaPlane,
  FaGift,
  FaChevronUp,
  FaChevronDown,
  FaRoute,
  FaArrowDown,
} from "react-icons/fa";
import { checkFixedRoute } from "../services/routesService";
import { calculateCabPrice } from "../utils/priceCalculator";
import { getDistanceBetweenCities } from "../utils/distanceCalculator";
import { API_URL } from "../config/apiConfig";
import TripDetailsSummary from "../components/TripDetailsSummary";

// Utility function to format date in DD-MM-YYYY format
const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return "Not set";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    return "Invalid date";
  }
};

const CarSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get authentication state from Redux store
  const { user } = useSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(true);
  const [cabOptions, setCabOptions] = useState([]);
  const [selectedCab, setSelectedCab] = useState(null);
  const [distance, setDistance] = useState(null);
  const [cabPrices, setCabPrices] = useState({});
  const [baseRate, setBaseRate] = useState(12);
  const [error, setError] = useState(null);
  const [isFixedRoute, setIsFixedRoute] = useState(false);
  const [fixedRouteData, setFixedRouteData] = useState(null);

  // Airport and rental package data
  const [airports, setAirports] = useState([]);
  const [rentalPackages, setRentalPackages] = useState([]);
  const [airportsLoading, setAirportsLoading] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(true);

  // Trip time editing states
  const [isEditingTimes, setIsEditingTimes] = useState(false);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [timeValidationError, setTimeValidationError] = useState("");

  // Modal state for date/time selection
  const [showDateModal, setShowDateModal] = useState(false);
  const [isRouteExpanded, setIsRouteExpanded] = useState(false);

  // Extract query parameters from URL
  const queryParams = new URLSearchParams(location.search);

  // Debug logging to track parameter extraction
  console.log("🔍 CarSelection - Current location:", location);
  console.log("🔍 CarSelection - Raw search params:", location.search);
  console.log("🔍 CarSelection - Parsed params:", Object.fromEntries(queryParams.entries()));

  const fromCity = queryParams.get("from") || queryParams.get("city") || "";
  const toCity = queryParams.get("to") || "";
  const fromLocation = queryParams.get("fromLocation") || fromCity;
  const toLocation = queryParams.get("toLocation") || toCity;
  const dateStr = queryParams.get("date");
  const timeStr = queryParams.get("time") || "10:00";
  const time = timeStr; // Alias for backward compatibility
  const journeyType = queryParams.get("journeyType") || "outstation";
  const tripType = queryParams.get("tripType") || "oneWay";
  const returnDateStr = queryParams.get("returnDate");
  const returnTimeStr = queryParams.get("returnTime");
  const serviceType = queryParams.get("service"); // rental, airport, or null for outstation
  const packageName = queryParams.get("package"); // for rental services
  const airportCode = queryParams.get("airport"); // for airport services
  const transferDirection = queryParams.get("direction"); // to/from for airport services
  const viaCitiesParam = queryParams.get("via"); // via cities for multi-city bookings
  const viaCities = viaCitiesParam
    ? viaCitiesParam.split(",").map((city) => city.trim())
    : [];

  // Debug extracted values
  console.log("🎯 CarSelection - Extracted values:", {
    fromCity, toCity, serviceType, packageName, dateStr, timeStr
  });

  // Fetch airports data
  const fetchAirports = async () => {
    try {
      setAirportsLoading(true);
      const response = await axios.get(`${API_URL}/airport`);
      if (response.data.success) {
        setAirports(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch airports:", error);
      toast.error("Failed to load airport transfers");
    } finally {
      setAirportsLoading(false);
    }
  };

  // Fetch rental packages data
  const fetchRentalPackages = async () => {
    try {
      setPackagesLoading(true);
      const response = await axios.get(`${API_URL}/rental-package`);
      if (response.data.success) {
        setRentalPackages(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch rental packages:", error);
      toast.error("Failed to load rental packages");
    } finally {
      setPackagesLoading(false);
    }
  };

  // Initialize trip time values when component mounts
  useEffect(() => {
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0];

    // Parse date from query parameter
    let dateFromQuery = queryParams.get("date");
    if (dateFromQuery) {
      // Extract date part from ISO string and format to 'YYYY-MM-DD'
      const dateObj = new Date(dateFromQuery);
      if (!isNaN(dateObj.getTime())) {
        dateFromQuery = dateObj.toISOString().split("T")[0];
      } else {
        dateFromQuery = formattedToday; // Fallback if invalid
      }
    } else {
      dateFromQuery = formattedToday;
    }
    setPickupDate(dateFromQuery);

    // Parse time from query parameter
    let timeFromQuery = queryParams.get("time");
    if (timeFromQuery) {
      // Remove any non-time characters and convert 12-hour to 24-hour format
      timeFromQuery = timeFromQuery
        .replace(/[^0-9:apm]/g, "")
        .trim()
        .toLowerCase();
      let [timePart, modifier] = timeFromQuery.split(/\s/);
      let [hours, minutes] = timePart.split(":");
      hours = parseInt(hours, 10);
      if (isNaN(hours) || isNaN(minutes)) {
        timeFromQuery = "10:00"; // Default if invalid
      } else {
        if (modifier === "pm" && hours < 12) hours += 12;
        if (modifier === "am" && hours === 12) hours = 0;
        timeFromQuery = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      }
    } else {
      // Use current time as default instead of hardcoded 10:00
      const now = new Date();
      const currentHours = now.getHours().toString().padStart(2, "0");
      const currentMinutes = now.getMinutes().toString().padStart(2, "0");
      timeFromQuery = `${currentHours}:${currentMinutes}`;
    }
    setPickupTime(timeFromQuery);

    // Similar logic for return date and time if round trip
    if (tripType === "roundTrip") {
      let returnDateFromQuery = queryParams.get("returnDate");
      if (returnDateFromQuery) {
        const returnDateObj = new Date(returnDateFromQuery);
        if (!isNaN(returnDateObj.getTime())) {
          returnDateFromQuery = returnDateObj.toISOString().split("T")[0];
        } else {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          returnDateFromQuery = tomorrow.toISOString().split("T")[0];
        }
      } else {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        returnDateFromQuery = tomorrow.toISOString().split("T")[0];
      }
      setReturnDate(returnDateFromQuery);

      let returnTimeFromQuery = queryParams.get("returnTime");
      if (returnTimeFromQuery) {
        returnTimeFromQuery = returnTimeFromQuery
          .replace(/[^0-9:apm]/g, "")
          .trim()
          .toLowerCase();
        let [timePart, modifier] = returnTimeFromQuery.split(/\s/);
        let [hours, minutes] = timePart.split(":");
        hours = parseInt(hours, 10);
        if (isNaN(hours) || isNaN(minutes)) {
          returnTimeFromQuery = "10:00"; // Default if invalid
        } else {
          if (modifier === "pm" && hours < 12) hours += 12;
          if (modifier === "am" && hours === 12) hours = 0;
          returnTimeFromQuery = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        }
      } else {
        returnTimeFromQuery = "10:00"; // Default time if not provided
      }
      setReturnTime(returnTimeFromQuery);
    }
  }, [queryParams, tripType]);

  // Validate trip times
  const validateTripTimes = () => {
    setTimeValidationError("");

    try {
      const pickupDateTime = new Date(
        `${pickupDate}T${pickupTime} ${pickupTime.includes("AM") ? "AM" : "PM"}`,
      );
      const now = new Date(
        new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
      );

      // Simplified pickup time validation as per user request: removed 30-minute buffer
      if (pickupDateTime < now) {
        setTimeValidationError("Pickup time cannot be in the past");
        return false;
      }

      // For round trips, validate return date/time
      if (tripType === "roundTrip" && returnDate) {
        const returnDateTime = new Date(
          `${returnDate}T${returnTime} ${returnTime.includes("AM") ? "AM" : "PM"}`,
        );

        // Ensure return is after pickup with minimum 1 hour difference
        const minReturnTime = new Date(pickupDateTime.getTime() + 60 * 60000); // pickup time + 1 hour

        if (returnDateTime < minReturnTime) {
          setTimeValidationError(
            "Return time must be at least 1 hour after pickup time",
          );
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Date validation error:", error);
      setTimeValidationError("Invalid date format");
      return false;
    }
  };

  // Update trip times and refresh cab prices
  const handleTimeUpdate = () => {
    if (!validateTripTimes()) {
      return;
    }

    // Update URL parameters with new times
    const updatedParams = new URLSearchParams(location.search);
    updatedParams.set("date", pickupDate);
    updatedParams.set("time", pickupTime);

    if (tripType === "roundTrip") {
      updatedParams.set("returnDate", returnDate);
      updatedParams.set("returnTime", returnTime);
    }

    // Update URL without refreshing the page
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${updatedParams.toString()}`,
    );

    // Exit editing mode
    setIsEditingTimes(false);

    // Toast notification
    toast.success("Trip times updated successfully");

    // Optionally: refresh cab prices if they depend on time/date
    // fetchCabData();
  };

  // State for popular route data
  const [isPopularRoute, setIsPopularRoute] = useState(false);
  const [popularRouteData, setPopularRouteData] = useState(null);

  // Function to check if route exists in popular routes
  const checkPopularRoute = async (fromCity, toCity) => {
    try {
      // Clean city names - remove state suffixes and extra spaces
      const cleanFromCity = fromCity.split(",")[0].trim();
      const cleanToCity = toCity.split(",")[0].trim();

      const response = await axios.get(`${API_URL}/popular-routes/find`, {
        params: {
          from: cleanFromCity,
          to: cleanToCity,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Route not found
      }
      throw error;
    }
  };

  // Function to calculate distance between locations using Google Maps API
  const calculateDistance = async (fromLocation, toLocation) => {
    return new Promise((resolve, reject) => {
      try {
        // Check if Google Maps API is loaded
        if (
          !window.google ||
          !window.google.maps ||
          !window.google.maps.DistanceMatrixService
        ) {
          console.error("Google Maps API is not loaded");
          throw new Error("Maps API not available");
        }

        const service = new window.google.maps.DistanceMatrixService();

        service.getDistanceMatrix(
          {
            origins: [fromLocation],
            destinations: [toLocation],
            travelMode: "DRIVING",
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (
              status === "OK" &&
              response.rows[0].elements[0].status === "OK"
            ) {
              const distanceInKm =
                response.rows[0].elements[0].distance.value / 1000; // Convert meters to km
              console.log(
                `Distance from ${fromLocation} to ${toLocation}: ${distanceInKm} km`,
              );
              resolve(distanceInKm);
            } else {
              console.error("Error from Google Maps API:", status, response);

              // Fallback to known distances for common routes
              const knownDistances = {
                "rajkot,ahmedabad": 214.47,
                "ahmedabad,rajkot": 214.47,
                "rajkot,delhi": 1140,
                "delhi,rajkot": 1140,
                "mumbai,pune": 149.5,
                "pune,mumbai": 149.5,
                "mumbai,delhi": 1420,
                "delhi,mumbai": 1420,
              };

              const routeKey = `${fromLocation.toLowerCase()},${toLocation.toLowerCase()}`;
              if (knownDistances[routeKey]) {
                console.log(
                  `Using known distance for ${routeKey}: ${knownDistances[routeKey]} km`,
                );
                resolve(knownDistances[routeKey]);
              } else {
                // If no known distance, use a default of 100km to prevent errors
                console.warn("No distance data available, using default 100km");
                resolve(100);
              }
            }
          },
        );
      } catch (error) {
        console.error("Error in calculateDistance:", error);
        // Return a default distance to prevent the app from breaking
        resolve(100);
      }
    });
  };

  // Function to calculate total distance with via cities
  const calculateDistanceWithViaCities = async (
    startCity,
    viaCities,
    endCity,
  ) => {
    console.log(
      `Calculating distance with via cities: ${startCity} → ${viaCities.join(" → ")} → ${endCity}`,
    );

    try {
      let totalDistance = 0;
      const segments = [];

      // Create array of all cities in order: [start, via1, via2, ..., end]
      const allCities = [startCity, ...viaCities, endCity];

      // Calculate distance for each segment
      for (let i = 0; i < allCities.length - 1; i++) {
        const fromCity = allCities[i];
        const toCity = allCities[i + 1];

        console.log(`Calculating segment ${i + 1}: ${fromCity} → ${toCity}`);
        const segmentDistance = await calculateDistance(fromCity, toCity);

        segments.push({
          from: fromCity,
          to: toCity,
          distance: segmentDistance,
        });

        totalDistance += segmentDistance;
        console.log(`Segment ${i + 1} distance: ${segmentDistance} km`);
      }

      console.log(`Total distance calculation:`);
      console.log(`Segments:`, segments);
      console.log(`Total distance: ${totalDistance} km`);

      return {
        totalDistance,
        segments,
      };
    } catch (error) {
      console.error("Error calculating distance with via cities:", error);
      // Fallback: estimate based on direct distance
      const directDistance = await calculateDistance(startCity, endCity);
      const estimatedTotal = directDistance * 1.3; // Add 30% for via cities
      console.log(`Fallback: estimated total distance ${estimatedTotal} km`);
      return {
        totalDistance: estimatedTotal,
        segments: [
          {
            from: startCity,
            to: endCity,
            distance: estimatedTotal,
          },
        ],
      };
    }
  };

  // Using the centralized price calculation function from utils
  const calculatePrice = (cab, dist) => {
    // Use rounded distance for consistent calculation
    const roundedDist = Math.round(dist);
    return calculateCabPrice(cab, roundedDist);
  };

  // Helper function to get cab price based on base rate per km only
  const calculateDynamicPrice = (cab) => {
    // Get base price per km from cab or use default
    const baseKmPrice = Number(cab.baseKmPrice) || 11.88;
    const extraKmRate = Number(cab.extraFarePerKm) || baseKmPrice * 1.2;

    // Use rounded distance for calculation to match display
    const roundedDistance = Math.round(distance || 0);

    // Calculate total price as base rate * rounded distance
    const totalPrice = baseKmPrice * roundedDistance;

    return {
      basePrice: Math.round(totalPrice),
      extraKmPrice: 0, // Not used in calculation
      totalPrice: Math.round(totalPrice),
      includedKm: 0, // Not used
      extraKm: 0, // Not used
      baseKmPrice,
      extraKmRate: Math.round(extraKmRate), // Show extra km rate in UI
    };
  };

  // Function to process fixed route cabs
  const processFixedRouteCabs = (fixedRoutes) => {
    const fixedCabs = fixedRoutes.map((route) => {
      const cabDetails = route.cabType;

      // Skip if cabDetails is null or undefined
      if (!cabDetails) {
        console.warn("Route found with null cabType:", route);
        return null;
      }

      return {
        _id: cabDetails._id,
        name: cabDetails.name,
        description: cabDetails.description || "Fixed route cab",
        category: cabDetails.category || "sedan",
        basePrice: route.price,
        price: route.price, // Same price for fixed routes
        totalPrice: route.price, // All inclusive price
        includedKm: 0, // No included kilometers as per simplified approach
        seatingCapacity: cabDetails.seatingCapacity || 4,
        luggageCapacity: cabDetails.luggageCapacity || 2,
        imageUrl:
          cabDetails.imageUrl ||
          "https://via.placeholder.com/300x200?text=Cab+Image",
        acType: cabDetails.acType || "AC",
        isFixedRoute: true,
        fixedRouteId: route._id,
        estimatedTime: route.estimatedTime,
        distance: route.distance,
        // Calculate price breakdown for display purposes
        priceBreakdown: {
          basePrice: route.price,
          taxes: 0, // Already included in fixed price
          tollCharges: 0, // Already included
          stateCharges: 0, // Already included
          total: route.price,
        },
      };
    }).filter(cab => cab !== null); // Filter out null values

    setCabOptions(fixedCabs);
    if (fixedCabs.length > 0) {
      setSelectedCab(fixedCabs[0]);
    }
  };

  // Note: We're using a simplified pricing model: price = distance * rate
  // No additional fees or calculations are needed

  // Helper function to get cab price - uses admin panel prices for airports/rentals
  const getCabPrice = (cab) => {
    // Null check for cab object
    if (!cab || !cab.name) {
      console.warn("getCabPrice called with invalid cab:", cab);
      return {
        originalPrice: 0,
        totalPrice: 0,
        basePrice: 0,
        extraKmPrice: 0,
        includedKm: 0,
        extraKm: 0,
        baseKmPrice: 0,
        extraKmRate: 0,
      };
    }

    // Check if this is an airport transfer or rental package journey
    const queryParams = new URLSearchParams(location.search);
    const service = queryParams.get("service");
    const packageName = queryParams.get("package");
    const airportCode = queryParams.get("airport");

    // For airport transfers - use admin panel pricing
    if (service === "airport" && airportCode) {
      const airport = airports.find((a) => a.code === airportCode);
      if (airport) {
        // Check service cities first
        if (airport.serviceCities && airport.serviceCities.length > 0) {
          for (const serviceCity of airport.serviceCities) {
            const cabPrice = cab.name ? serviceCity.cabPrices?.[cab.name] : null;
            if (cabPrice) {
              return {
                originalPrice: cabPrice,
                totalPrice: cabPrice,
                basePrice: cabPrice,
                extraKmPrice: 0,
                includedKm: 0,
                extraKm: 0,
                baseKmPrice: 0,
                extraKmRate: 0,
              };
            }
          }
        }
        // Fallback to legacy cab type pricing
        if (airport.cabTypePrices && airport.cabTypePrices.length > 0 && cab.name) {
          const cabTypePricing = airport.cabTypePrices.find(
            (cp) => cp.cabTypeId?.name === cab.name,
          );
          if (cabTypePricing) {
            return {
              originalPrice: cabTypePricing.price,
              totalPrice: cabTypePricing.price,
              basePrice: cabTypePricing.price,
              extraKmPrice: 0,
              includedKm: 0,
              extraKm: 0,
              baseKmPrice: 0,
              extraKmRate: 0,
            };
          }
        }
      }
    }

    // For rental packages - use admin panel pricing
    if (service === "rental" && packageName) {
      const pkg = rentalPackages.find((p) => p.name === packageName);
      if (pkg) {
        // Check service cities first
        if (pkg.serviceCities && pkg.serviceCities.length > 0) {
          for (const serviceCity of pkg.serviceCities) {
            const cabPrice = cab.name ? serviceCity.cabPrices?.[cab.name] : null;
            if (cabPrice) {
              return {
                originalPrice: cabPrice,
                totalPrice: cabPrice,
                basePrice: cabPrice,
                extraKmPrice: 0,
                includedKm: 0,
                extraKm: 0,
                baseKmPrice: 0,
                extraKmRate: 0,
              };
            }
          }
        }
        // Fallback to legacy cab type pricing
        if (pkg.cabTypePrices && pkg.cabTypePrices.length > 0 && cab.name) {
          const cabTypePricing = pkg.cabTypePrices.find(
            (cp) => cp.cabTypeId?.name === cab.name,
          );
          if (cabTypePricing) {
            return {
              originalPrice: cabTypePricing.price,
              totalPrice: cabTypePricing.price,
              basePrice: cabTypePricing.price,
              extraKmPrice: 0,
              includedKm: 0,
              extraKm: 0,
              baseKmPrice: 0,
              extraKmRate: 0,
            };
          }
        }
      }
    }

    // For rental packages with fixed pricing (similar to popular routes)
    if (cab.isRentalPackage && cab.rentalPackagePrice) {
      return {
        originalPrice: cab.rentalPackagePrice,
        totalPrice: cab.rentalPackagePrice,
        basePrice: cab.rentalPackagePrice,
        extraKmPrice: 0,
        includedKm: 0,
        extraKm: 0,
        baseKmPrice: 0,
        extraKmRate: 0,
      };
    }
    // For popular routes with fixed pricing
    else if (cab.isPopularRoute && cab.popularRoutePrice) {
      return {
        originalPrice: cab.popularRoutePrice,
        totalPrice: cab.popularRoutePrice,
        basePrice: cab.popularRoutePrice,
        extraKmPrice: 0,
        includedKm: 0,
        extraKm: 0,
        baseKmPrice: cab.baseKmPrice || 0,
        extraKmRate: 0,
      };
    }
    // For fixed routes, return a simplified price object
    else if (cab.isFixedRoute) {
      return {
        originalPrice: cab.price,
        totalPrice: cab.price,
        basePrice: cab.price,
        extraKmPrice: 0,
        includedKm: 0,
        extraKm: 0,
        baseKmPrice: 0,
        extraKmRate: 0,
      };
    } else {
      // Use the dynamic pricing function which returns the full price object
      const priceInfo = calculateDynamicPrice(cab);
      // Ensure we have all required fields
      return {
        originalPrice: priceInfo.totalPrice || 0,
        totalPrice: priceInfo.totalPrice || 0,
        basePrice: priceInfo.basePrice || 0,
        extraKmPrice: priceInfo.extraKmPrice || 0,
        includedKm: priceInfo.includedKm || 0,
        extraKm: priceInfo.extraKm || 0,
        baseKmPrice: priceInfo.baseKmPrice || 0,
        extraKmRate: priceInfo.extraKmRate || 0,
      };
    }
  };

  // Shared handler for booking button clicks
  const handleCabBooking = (e, cab) => {
    e.stopPropagation();

    // Set as selected cab
    setSelectedCab(cab);

    // Always show the Trip Details modal regardless of authentication status
    // Authentication will be checked when user clicks "Continue to Booking"
    setShowDateModal(true);
  };

  // Function to fetch real cab data from the database
  const fetchCabData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setIsFixedRoute(false);
      setFixedRouteData(null);
      setIsPopularRoute(false);
      setPopularRouteData(null);

      // FIRST: Check if this is a popular route with fixed pricing (skip for rental services)
      let popularRoute = null;
      if (serviceType !== "rental") {
        try {
          popularRoute = await checkPopularRoute(fromCity, toCity);

          if (popularRoute) {
            console.log(
              "Found popular route with fixed pricing:",
              popularRoute,
            );
            setIsPopularRoute(true);
            setPopularRouteData(popularRoute);

            // Set fixed distance from popular route
            const fixedDistance =
              parseFloat(popularRoute.distance.replace(/[^\d.]/g, "")) || 100;
            setDistance(fixedDistance);
          }
        } catch (popularRouteError) {
          console.log(
            "No popular route found, proceeding with dynamic pricing",
          );
          // Continue with normal flow if popular route check fails
        }
      } else {
        console.log("Rental service detected - skipping popular routes check");
      }

      // Check if this is a fixed route (skip for rental and airport services)
      if (serviceType !== "rental" && serviceType !== "airport") {
        try {
          // First, get city IDs from names
          const cityResponse = await axios.get(`${API_URL}/city`);
          if (cityResponse.data.success) {
            const cities = cityResponse.data.data;
            const fromCityObj = cities.find(
              (city) => city.name.toLowerCase() === fromCity.toLowerCase(),
            );
            const toCityObj = cities.find(
              (city) => city.name.toLowerCase() === toCity.toLowerCase(),
            );

            if (fromCityObj && toCityObj) {
              // Check for fixed route
              const fixedRoutes = await checkFixedRoute(
                fromCityObj._id,
                toCityObj._id,
              );

              if (fixedRoutes && fixedRoutes.length > 0) {
                // We have fixed routes!
                setIsFixedRoute(true);
                setFixedRouteData(fixedRoutes);

                // Process the fixed routes into cab options
                processFixedRouteCabs(fixedRoutes);
                setIsLoading(false);
                return;
              }
            }
          }
        } catch (fixedRouteError) {
          console.error("Error checking for fixed route:", fixedRouteError);
          // Continue with normal flow if fixed route check fails
        }
      } else {
        console.log(
          "Rental/Airport service detected - skipping fixed routes check",
        );
      }

      // For non-popular routes, calculate distance and fetch cabs normally

      // Always calculate the distance to ensure we have the latest value
      try {
        // For Rajkot to Delhi, we know this is a special route with fixed distance of 1140 km
        let dist;
        if (
          (fromCity.toLowerCase() === "rajkot" &&
            toCity.toLowerCase() === "delhi") ||
          (fromCity.toLowerCase() === "delhi" &&
            toCity.toLowerCase() === "rajkot")
        ) {
          dist = 1140; // Actual road distance from Rajkot to Delhi in km
          console.log(`Using known distance for Rajkot-Delhi route: ${dist}km`);
        } else {
          // For other routes, calculate distance from API
          if (viaCities.length > 0) {
            console.log(
              `Route has ${viaCities.length} via cities: ${viaCities.join(", ")}`,
            );
            const distanceResult = await calculateDistanceWithViaCities(
              fromCity,
              viaCities,
              toCity,
            );
            dist = distanceResult.totalDistance;
            // Store segments for later use if needed
            window.currentRouteSegments = distanceResult.segments;
            console.log(
              `Successfully calculated total distance via cities: ${dist}km`,
            );
          } else {
            dist = await calculateDistance(fromCity, toCity);
            console.log(
              `Successfully calculated direct distance from ${fromCity} to ${toCity}: ${dist}km`,
            );
          }
        }

        // Always update the distance state
        setDistance(dist);
      } catch (distError) {
        console.error("Error calculating distance:", distError);
        setError("Could not calculate distance between locations.");
        setIsLoading(false);
        return;
      }

      console.log(`Searching for cabs in ${fromCity}`);

      let response;

      // For rental services, fetch cab types directly instead of checking availability
      if (serviceType === "rental") {
        console.log("Rental service - fetching cab types directly");
        response = await axios.get(`${API_URL}/cabtype/all`);

        // Process the cabs to add rental package pricing similar to popular routes
        if (response.data.success && response.data.data) {
          const cabs = response.data.data;
          const selectedPackage = rentalPackages.find(
            (pkg) => pkg.name === packageName,
          );

          if (selectedPackage) {
            console.log(
              "Processing rental package pricing for cabs:",
              selectedPackage,
            );

            // Add rental package pricing to each cab, similar to popular routes
            const cabsWithRentalPricing = cabs.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id).map((cab) => {
              // Look for cab price in the selected package
              let cabPrice = null;

              // Check service cities first
              if (
                selectedPackage.serviceCities &&
                selectedPackage.serviceCities.length > 0
              ) {
                for (const serviceCity of selectedPackage.serviceCities) {
                  if (
                    serviceCity.cabPrices &&
                    cab.name &&
                    serviceCity.cabPrices[cab.name]
                  ) {
                    cabPrice = serviceCity.cabPrices[cab.name];
                    break;
                  }
                }
              }

              // Fallback to legacy cab type pricing
              if (
                !cabPrice &&
                selectedPackage.cabTypePrices &&
                selectedPackage.cabTypePrices.length > 0
              ) {
                const cabTypePricing = selectedPackage.cabTypePrices.find(
                  (cp) =>
                    (cab.name && cp.cabTypeId?.name === cab.name) ||
                    cp.cabTypeId?._id === cab._id,
                );
                if (cabTypePricing) {
                  cabPrice = cabTypePricing.price;
                }
              }

              // If we found a price, mark this cab as having rental package pricing
              if (cabPrice) {
                return {
                  ...cab,
                  isRentalPackage: true,
                  rentalPackagePrice: cabPrice,
                  rentalPackageName: selectedPackage.name,
                  rentalPackageId: selectedPackage._id,
                };
              }

              return cab;
            });

            // Override the response data with processed cabs
            response.data.data = cabsWithRentalPricing;
          }
        }
      } else {
        // For outstation/airport services, check availability
        console.log("🚗 Checking cab availability for outstation/airport service");
        console.log("📍 API URL:", API_URL);
        console.log("📍 Request params:", {
          journeyType,
          pickupLocation: fromCity,
          dropLocation: toCity,
          pickupDate: dateStr,
          returnDate: tripType === "roundTrip" ? dateStr : null,
        });
        
        try {
          response = await axios.post(`${API_URL}/cab/available`, {
            journeyType: journeyType,
            pickupLocation: fromCity,
            dropLocation: toCity,
            pickupDate: dateStr,
            returnDate: tripType === "roundTrip" ? dateStr : null,
          });
          
          console.log("✅ Availability check response:", response.data);
          
          // If availability check returns no cabs, fallback to fetching all cabs
          if (!response.data.success || !response.data.data || response.data.data.length === 0) {
            console.log("⚠️ No cabs from availability check, fetching all cabs as fallback");
            console.log("📍 Fallback API URL:", `${API_URL}/cabtype/all`);
            
            try {
              response = await axios.get(`${API_URL}/cabtype/all`);
              console.log("✅ Fallback response:", response.data);
            } catch (fallbackError) {
              console.error("❌ Fallback also failed:", fallbackError);
              console.error("❌ Error details:", {
                message: fallbackError.message,
                response: fallbackError.response?.data,
                status: fallbackError.response?.status,
              });
              throw fallbackError;
            }
          }
        } catch (availabilityError) {
          console.error("❌ Availability check failed:", availabilityError);
          console.error("❌ Error details:", {
            message: availabilityError.message,
            response: availabilityError.response?.data,
            status: availabilityError.response?.status,
            url: `${API_URL}/cab/available`,
          });
          
          // Fallback to fetching all cab types if availability check fails
          console.log("🔄 Attempting fallback to fetch all cabs...");
          try {
            response = await axios.get(`${API_URL}/cabtype/all`);
            console.log("✅ Fallback successful:", response.data);
          } catch (fallbackError) {
            console.error("❌ Fallback also failed:", fallbackError);
            console.error("❌ Complete failure - no cabs can be fetched");
            throw new Error(
              `Failed to fetch cabs from both endpoints. ` +
              `Primary error: ${availabilityError.message}. ` +
              `Fallback error: ${fallbackError.message}`
            );
          }
        }
      }

      console.log("📊 Response data structure:", {
        hasData: !!response.data,
        success: response.data?.success,
        dataLength: response.data?.data?.length,
        dataType: Array.isArray(response.data?.data) ? 'array' : typeof response.data?.data,
      });

      if (!response.data.success) {
        console.error("❌ Response not successful:", response.data);
        throw new Error(response.data.message || "Failed to fetch cabs");
      }

      let cabs = response.data.data;
      console.log(`Found ${cabs.length} cabs from backend in ${fromCity}`);

      // CRITICAL FIX: Adjust any unrealistically low baseKmPrice values
      // Some cabs in database may have baseKmPrice as absolute values (10, 14) instead of per-km rates
      cabs = cabs.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id).map((cab) => {
        // If baseKmPrice is unrealistically low for a per-km rate (< 5 rupees per km)
        // interpret as a base price and convert to a more realistic per-km rate
        if (
          (cab.baseKmPrice < 5 ||
            cab.baseKmPrice === 10 ||
            cab.baseKmPrice === 14) &&
          distance > 50
        ) {
          const adjustedCab = { ...cab };
          // Store original price for reference
          adjustedCab.originalBasePrice = cab.baseKmPrice;

          // Set to a more realistic per-km rate (based on cab category)
          if (cab.category?.toLowerCase().includes("luxury")) {
            adjustedCab.baseKmPrice = 18; // Luxury cab rate
          } else if (cab.category?.toLowerCase().includes("suv")) {
            adjustedCab.baseKmPrice = 15; // SUV rate
          } else {
            adjustedCab.baseKmPrice = 12; // Standard sedan rate
          }

          // Also ensure there's some extraFarePerKm if it's missing
          if (!adjustedCab.extraFarePerKm || adjustedCab.extraFarePerKm < 1) {
            adjustedCab.extraFarePerKm = adjustedCab.baseKmPrice * 1.2;
          }

          console.log(
            `Adjusted cab ${cab.name} baseKmPrice from ${cab.baseKmPrice} to ${adjustedCab.baseKmPrice} rupees/km`,
          );
          return adjustedCab;
        }
        return cab;
      });

      // Handle no cabs found situation
      if (!cabs || cabs.length === 0) {
        console.warn("No cabs found after all attempts");
        setError(
          `No cabs available for this route. This could be because:\n` +
          `• No cabs are configured in the system\n` +
          `• The route is not covered by our service\n\n` +
          `Please try a different route or contact support.`,
        );
        setIsLoading(false);
        return cabs || [];
      }

      // If popular route found, modify cabs with popular route pricing and distance
      if (popularRoute) {
        console.log("Applying popular route pricing to regular cabs");
        const fixedDistance =
          parseFloat(popularRoute.distance.replace(/[^\d.]/g, "")) || 100;

        cabs = cabs.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id).map((cab) => {
          // Find the specific price for this cab type from the popular route
          let cabPrice = null;

          if (
            popularRoute.cabTypePrices &&
            popularRoute.cabTypePrices.length > 0
          ) {
            const cabTypePrice = popularRoute.cabTypePrices.find(
              (ctp) =>
                ctp.cabTypeId._id === cab._id || ctp.cabTypeId === cab._id,
            );
            if (cabTypePrice) {
              cabPrice = cabTypePrice.price;
            }
          }

          // If no specific price found for this cab type, use dynamic pricing
          if (!cabPrice) {
            return cab; // Keep original cab without modification
          }

          // Return modified cab with popular route pricing but keep all other properties
          return {
            ...cab,
            isPopularRoute: true,
            popularRouteId: popularRoute._id,
            // Override pricing fields
            baseKmPrice: cabPrice / fixedDistance, // Calculate per km rate
            totalPrice: cabPrice,
            popularRoutePrice: cabPrice,
            popularRouteDistance: popularRoute.distance,
          };
        });
      }

      return cabs;
    } catch (error) {
      console.error("Error fetching cab data:", error);
      throw error;
    }
  };

  useEffect(() => {
    // Validation logic based on service type
    if (serviceType === "rental") {
      // For rental services, only fromCity (selected city) is required
      if (!fromCity) {
        toast.error("Missing location information for rental service");
        navigate("/");
        return;
      }
    } else if (serviceType === "airport") {
      // For airport services, validate based on direction
      if (transferDirection === "to") {
        if (!fromCity) {
          toast.error("Missing pickup city for airport transfer");
          navigate("/");
          return;
        }
      } else if (transferDirection === "from") {
        if (!toCity) {
          toast.error("Missing drop city for airport transfer");
          navigate("/");
          return;
        }
      }
    } else {
      // For outstation services, both cities are required
      if (!fromCity || !toCity) {
        toast.error("Missing location information");
        navigate("/");
        return;
      }

      // Validate that pickup and destination cities are different
      if (fromCity === toCity) {
        toast.error("Pickup and destination cities cannot be the same");
        navigate("/");
        return;
      }
    }

    const loadRealCabData = async () => {
      try {
        setIsLoading(true);
        // Reset states to prevent showing stale data
        setCabOptions([]);
        setError("");
        setSelectedCab(null);

        // 1. Fetch real cab data from the API
        const cabs = await fetchCabData();

        // 2. Calculate pricing for each cab based on distance using our utility function
        const prices = {};
        if (Array.isArray(cabs)) {
          cabs.forEach((cab) => {
            prices[cab._id] = getCabPrice(cab);
          });
        }

        setCabPrices(prices);

        // Filter cabs based on service type to prevent showing wrong service data
        let filteredCabs = cabs || [];

        console.log("🚗 Raw cabs data from API:", cabs);
        console.log("🔍 Service type:", serviceType);
        console.log("🔍 Journey type:", journeyType);

        // For outstation services, only show regular cabs (not airport or rental specific)
        if (!serviceType || serviceType === "outstation") {
          console.log(
            "📌 Filtering for outstation service - excluding airport/rental cabs",
          );
          filteredCabs = filteredCabs.filter(
            (cab) =>
              !cab.isAirportTransfer &&
              !cab.isRentalPackage &&
              (!cab.serviceType || cab.serviceType === "outstation"),
          );
          console.log("✅ Filtered cabs for outstation:", filteredCabs);
        }
        // For airport services, only show airport-related cabs
        else if (serviceType === "airport") {
          filteredCabs = filteredCabs.filter(
            (cab) =>
              cab.isAirportTransfer ||
              !cab.serviceType ||
              cab.serviceType === "airport",
          );
        }
        // For rental services, only show rental-related cabs
        else if (serviceType === "rental") {
          filteredCabs = filteredCabs.filter(
            (cab) =>
              cab.isRentalPackage ||
              !cab.serviceType ||
              cab.serviceType === "rental",
          );
        }

        setCabOptions(filteredCabs);
      } catch (error) {
        console.error("Error loading cab data:", error);
        setError("Failed to load cab options. Please try again.");

        // Try to fetch from local storage cache if API fails
        try {
          const cachedCabs = JSON.parse(
            localStorage.getItem("cabTypes") || "[]",
          );
          if (cachedCabs && cachedCabs.length > 0) {
            console.log("Using cached cab data from localStorage");
            setCabOptions(cachedCabs);
          } else {
            // No cached data available either
            setCabOptions([]);
            setError("No cab data available. Please try again later.");
          }
        } catch (cacheError) {
          console.error("Error reading from cache:", cacheError);
          setCabOptions([]);
          setError("Failed to load cab options. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Load data based on service type
    const dataPromises = [loadRealCabData()];

    // Only load additional data if needed for specific service types
    if (serviceType === "airport") {
      dataPromises.push(fetchAirports());
    } else if (serviceType === "rental") {
      dataPromises.push(fetchRentalPackages());
    }

    Promise.all(dataPromises);
  }, [fromCity, toCity, journeyType, tripType, dateStr, serviceType, transferDirection, airportCode, packageName, navigate]);

  // Handle cab selection
  const handleSelectCab = (cab) => {
    setSelectedCab(cab);
  };

  // Handle booking button click
  const handleBookNow = () => {
    // Null check for selectedCab
    if (!selectedCab || !selectedCab.name) {
      return;
    }

    // Check if user is logged in - FIRST using Redux state, then fallback to localStorage
    let isLoggedIn = !!user; // Primary check using Redux state

    // Only check localStorage as fallback if Redux state doesn't have user
    if (!isLoggedIn) {
      try {
        // Try multiple localStorage formats as fallback
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
        isLoggedIn =
          (userInfo && userInfo.token) ||
          (localUser && localUser.token) ||
          !!localStorage.getItem("token");
      } catch (e) {
        console.error("Error checking local storage auth:", e);
      }
    }

    if (!isLoggedIn) {
      // Construct the current URL with all parameters
      const currentUrl = new URL(window.location.href);
      const redirectUrl = currentUrl.pathname + currentUrl.search;

      // Store current selection in localStorage for returning after login
      localStorage.setItem(
        "selectedCabBeforeLogin",
        JSON.stringify({
          id: selectedCab._id,
          name: selectedCab.name,
          fromCity: fromCity,
          toCity: toCity,
          fromLocation: fromLocation,
          toLocation: toLocation,
          distance: distance,
          date: dateStr,
          time: time,
          timestamp: new Date().toISOString(),
        }),
      );

      toast.info("Please log in to continue with your booking");

      // Redirect to login with the current URL for redirecting back after login
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    // If logged in, show date/time modal
    setShowDateModal(true);
  };

  // Handle proceeding after date/time selection
  const handleProceedBooking = () => {
    console.log("🚗 Continue to Booking clicked");

    // First validate the time inputs
    if (!validateTripTimes()) {
      // If validation fails, keep the modal open and show error
      toast.error(timeValidationError);
      return;
    }

    // Close the modal
    setShowDateModal(false);

    // Check if user is logged in - FIRST using Redux state, then fallback to localStorage
    let isLoggedIn = !!user; // Primary check using Redux state
    let userInfo = user || {};

    console.log("🔐 Authentication check:", {
      reduxUser: !!user,
      userToken: user?.token ? "exists" : "missing"
    });

    // Only check localStorage as fallback if Redux state doesn't have user
    if (!isLoggedIn) {
      try {
        // Format 1: Redux auth slice stores as 'user'
        const localUser = JSON.parse(localStorage.getItem("user") || "{}");
        if (localUser && localUser.token) {
          isLoggedIn = true;
          userInfo = localUser;
          console.log("✅ Found auth in localStorage format 1");
        }

        // Format 2: Some components might use 'userInfo'
        if (!isLoggedIn) {
          const userInfoData = JSON.parse(
            localStorage.getItem("userInfo") || "{}",
          );
          if (userInfoData && userInfoData.token) {
            isLoggedIn = true;
            userInfo = userInfoData;
            console.log("✅ Found auth in localStorage format 2");
          }
        }

        // Format 3: Direct token storage
        if (!isLoggedIn && localStorage.getItem("token")) {
          isLoggedIn = true;
          console.log("✅ Found direct token in localStorage");
        }
      } catch (e) {
        console.error("❌ Error checking local storage auth:", e);
      }
    }

    // Construct query parameters to pass to the next page
    const params = new URLSearchParams();
    params.append("from", fromCity);
    params.append("to", toCity);
    params.append("cabId", selectedCab._id);
    params.append("cabName", selectedCab.name);
    params.append("tripType", tripType);

    // Add via cities if present
    if (viaCities.length > 0) {
      params.append("via", viaCities.join(","));
      console.log("🛣��� Adding via cities to navigation:", viaCities.join(","));
    }

    // Preserve service-specific parameters for rental and airport services
    if (serviceType === "rental") {
      params.append("service", "rental");
      if (packageName) {
        params.append("package", packageName);
      }
      console.log("🚗 Rental service parameters added:", { packageName });
    } else if (serviceType === "airport") {
      params.append("service", "airport");
      if (airportCode) {
        params.append("airport", airportCode);
      }
      if (transferDirection) {
        params.append("direction", transferDirection);
      }
      console.log("✈️ Airport service parameters added:", { airportCode, transferDirection });
    }

    // Extract the actual numeric price value - ensure we pass a number, not an object
    let fareAmount;

    // For rental services, use rental package price
    if (serviceType === "rental" && packageName) {
      console.log("Using rental package pricing for:", packageName);
      const selectedPackage = rentalPackages.find(
        (pkg) => pkg.name === decodeURIComponent(packageName),
      );
      if (selectedPackage && selectedPackage.cabPrices) {
        const cabPrice = selectedPackage.cabPrices.find(
          (cp) =>
            cp.cabTypeId === selectedCab._id ||
            cp.cabTypeId === selectedCab.name,
        );
        if (cabPrice) {
          fareAmount = cabPrice.price;
          console.log(
            `Found rental package price: ₹${fareAmount} for ${selectedCab.name}`,
          );
        } else {
          console.warn(
            "No rental price found for this cab type, using base price",
          );
          fareAmount = selectedPackage.basePrice || 1000;
        }
      } else {
        console.warn("Rental package not found, using fallback price");
        fareAmount = 1000; // Fallback price for rental
      }
    }
    // For airport services, use airport-specific pricing
    else if (serviceType === "airport" && airportCode) {
      console.log("Using airport pricing for:", airportCode);
      const selectedAirport = airports.find(
        (airport) => airport.code === airportCode,
      );
      if (selectedAirport) {
        let airportPrice = null;

        // First check service cities with cab-specific pricing
        if (
          selectedAirport.serviceCities &&
          selectedAirport.serviceCities.length > 0
        ) {
          for (const serviceCity of selectedAirport.serviceCities) {
            if (
              serviceCity.cabPrices &&
              selectedCab.name &&
              serviceCity.cabPrices[selectedCab.name]
            ) {
              airportPrice = serviceCity.cabPrices[selectedCab.name];
              console.log(
                `Found airport price in service cities: ₹${airportPrice} for ${selectedCab.name}`,
              );
              break;
            }
          }
        }

        // Fallback to legacy cab type pricing
        if (
          !airportPrice &&
          selectedAirport.cabTypePrices &&
          selectedAirport.cabTypePrices.length > 0
        ) {
          const cabTypePricing = selectedAirport.cabTypePrices.find(
            (cp) =>
              (selectedCab.name && cp.cabTypeId?.name === selectedCab.name) ||
              cp.cabTypeId === selectedCab._id,
          );
          if (cabTypePricing) {
            airportPrice = cabTypePricing.price;
            console.log(
              `Found airport price in cab types: ₹${airportPrice} for ${selectedCab.name}`,
            );
          }
        }

        if (airportPrice) {
          fareAmount = airportPrice;
        } else {
          console.warn(
            "No airport price found for this cab type, using fallback",
          );
          fareAmount = 2000; // Fallback price for airport transfers
        }
      } else {
        console.warn("Airport not found, using fallback price");
        fareAmount = 2000; // Fallback price for airport transfers
      }
    }
    // Don't override rental or airport pricing
    else if (
      cabPrices[selectedCab._id] &&
      cabPrices[selectedCab._id].originalPrice
    ) {
      // Get the numeric value from the price object
      fareAmount = cabPrices[selectedCab._id].originalPrice;
    } else if (selectedCab.isFixedRoute && selectedCab.price) {
      // For fixed routes, use the fixed price
      fareAmount = selectedCab.price;
    } else {
      // Calculate dynamic price based on distance and extract the totalPrice
      const priceInfo = calculateDynamicPrice(selectedCab);
      fareAmount = priceInfo.totalPrice || 0;
    }

    // Ensure fareAmount is a number
    fareAmount = Number(fareAmount) || 0;

    // Ensure we have a realistic minimum fare for long distances
    const roundedDistance = Math.round(distance);
    if (roundedDistance > 100 && fareAmount < 1000) {
      // Apply a realistic per-km rate for long journeys (₹12-15 per km is typical)
      const ratePerKm = selectedCab.category?.toLowerCase().includes("luxury")
        ? 15
        : 12;
      fareAmount = Math.round(roundedDistance * ratePerKm);
      console.log(
        `Applying realistic fare calculation: ${roundedDistance}km × ₹${ratePerKm}/km = ₹${fareAmount}`,
      );
    }

    params.append("date", pickupDate);
    params.append("time", pickupTime);
    params.append("amount", fareAmount);
    params.append("distance", distance);

    // Set pickup location based on service type
    if (serviceType === "rental") {
      params.append("pickupLocation", fromCity);
    } else if (selectedCab.vehicleLocation) {
      params.append("pickupLocation", selectedCab.vehicleLocation);
    } else {
      params.append("pickupLocation", fromCity);
    }

    // Add user mobile number if available - for prefilling in the next form
    if (isLoggedIn && userInfo.phone) {
      params.append("mobile", userInfo.phone);
    }

    // Use the collected date/time values
    if (tripType === "roundTrip") {
      params.append("returnDate", returnDate);
      params.append("returnTime", returnTime);
    }

    // Store comprehensive booking intent data in local storage for login recovery
    const bookingIntent = {
      from: fromCity,
      to: toCity,
      cabId: selectedCab._id,
      cabName: selectedCab.name,
      type: tripType,
      serviceType: serviceType,
      packageName: packageName,
      airportCode: airportCode,
      transferDirection: transferDirection,
      viaCities: viaCities,
      amount: fareAmount, // Use the properly calculated fare amount
      distance: distance,
      pickupLocation: selectedCab.vehicleLocation || fromCity,
      date: pickupDate,
      time: pickupTime,
      returnDate: returnDate || "",
      returnTime: returnTime || "",
      timestamp: new Date().toISOString(),
    };

    console.log("💾 Storing booking intent:", bookingIntent);

    // Store selected cab data in local storage for persistence
    localStorage.setItem(
      "selectedCabData",
      JSON.stringify({
        id: selectedCab._id,
        name: selectedCab.name,
        basePrice: selectedCab.basePrice,
        imageUrl: selectedCab.imageUrl,
      }),
    );

    // Store booking intent for recovery after login
    localStorage.setItem("bookingIntent", JSON.stringify(bookingIntent));

    // Construct the complete booking URL with all parameters
    const bookingUrl = `/booking/customer-details?${params.toString()}`;

    console.log("🔗 Generated booking URL:", bookingUrl);
    console.log("📋 URL Parameters:", Object.fromEntries(params.entries()));

    // Check if user is logged in and redirect accordingly
    if (isLoggedIn) {
      console.log("✅ User is logged in, proceeding directly to booking");
      // Proceed directly to booking/payment page
      navigate(bookingUrl);
    } else {
      console.log("🔒 User not logged in, redirecting to login with redirect URL");
      // If not logged in, redirect to login with complete booking URL as redirect parameter
      const loginUrl = `/login?redirect=${encodeURIComponent(bookingUrl)}`;
      console.log("🔄 Login redirect URL:", loginUrl);

      toast.info("Please log in to continue with your booking");
      navigate(loginUrl);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Finding the best cabs for you...</p>
        </div>
      </div>
    );
  }

  // Defensive null checks for arrays
  const safeCabOptions = Array.isArray(cabOptions) ? cabOptions.filter(cab => cab && cab.name && typeof cab.name === 'string' && cab._id) : [];
  const safeRentalPackages = Array.isArray(rentalPackages) ? rentalPackages : [];
  const safeAirports = Array.isArray(airports) ? airports : [];

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Date/Time Selection Modal */}
      {showDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Trip Details</h3>
              <button
                onClick={() => setShowDateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            {/* Current trip summary */}
            <div className="bg-blue-50 p-4 rounded-lg mb-5">
              {serviceType === "rental" ? (
                <div>
                  <div className="flex items-center mb-2">
                    <FaGift className="text-blue-600 mr-2" />
                    <span className="font-medium">
                      Local Rental in {fromCity}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="inline-block">
                      Package: {packageName || "4 Hours/40 KM"}
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center mb-2">
                    <FaMapMarkerAlt className="text-blue-600 mr-2" />
                    <span className="font-medium">{fromCity}</span>
                    <FaArrowRight className="mx-2 text-gray-500" />
                    <span className="font-medium">{toCity}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="inline-block mr-3">
                      {tripType === "roundTrip"
                        ? "Round Trip"
                        : viaCities.length > 0
                        ? "Multi-City"
                        : "One Way"}
                    </span>
                    <span className="inline-block">
                      {distance ? Math.round(distance) : 0} km
                    </span>
                  </div>
                </div>
              )}
              <div className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Pickup Date:</span>{" "}
                {formatDateDDMMYYYY(pickupDate)}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Pickup Time:</span>{" "}
                {pickupTime || "Not set"}
              </div>
              {tripType === "roundTrip" && (
                <>
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">Return Date:</span>{" "}
                    {formatDateDDMMYYYY(returnDate)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">Return Time:</span>{" "}
                    {returnTime || "Not set"}
                  </div>
                </>
              )}
              <div className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Selected Cab:</span>{" "}
                {selectedCab?.name}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-800">
                Modify Travel Details
              </h4>
              {/* Pickup Date and Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FaCalendarAlt className="inline mr-2 text-blue-600" />
                  Pickup Date
                </label>
                <input
                  type="date"
                  value={pickupDate || new Date().toISOString().split("T")[0]}
                  onChange={(e) => setPickupDate(e.target.value)}
                  className="p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FaClock className="inline mr-2 text-blue-600" />
                  Pickup Time
                </label>
                <input
                  type="time"
                  value={
                    pickupTime ||
                    (() => {
                      const now = new Date();
                      return `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
                    })()
                  }
                  onChange={(e) => setPickupTime(e.target.value)}
                  className="p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Return Date and Time (only for round trip) */}
              {tripType === "roundTrip" && (
                <>
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FaCalendarAlt className="inline mr-2 text-blue-600" />
                      Return Date
                    </label>
                    <input
                      type="date"
                      value={
                        returnDate || new Date().toISOString().split("T")[0]
                      }
                      onChange={(e) => setReturnDate(e.target.value)}
                      className="p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min={pickupDate || new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <FaClock className="inline mr-2 text-blue-600" />
                      Return Time
                    </label>
                    <input
                      type="time"
                      value={returnTime || "10:00"}
                      onChange={(e) => setReturnTime(e.target.value)}
                      className="p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </>
              )}

              {timeValidationError && (
                <div className="text-red-500 text-sm mt-2">
                  {timeValidationError}
                </div>
              )}

              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setShowDateModal(false)}
                  className="bg-gray-200 text-gray-800 py-2 px-6 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedBooking}
                  className="bg-blue-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Continue to Booking
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Select Your Cab</h1>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6 border border-gray-200 transition-all duration-300 hover:shadow-xl">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <FaTaxi className="mr-2" />
                <span className="font-medium">Trip Details</span>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm">
                {serviceType === "airport"
                  ? "Airport Transfer"
                  : serviceType === "rental"
                  ? "Local Rental"
                  : tripType === "oneWay" ? "One Way" : "Round Trip"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
            {/* Route Details */}
            <div className="p-5 hover:bg-blue-50 transition-colors duration-200 relative group">
              <div className="flex items-start">
                <div className="bg-blue-100 p-3 rounded-full mr-4 shadow-sm">
                  {serviceType === "rental" ? (
                    <FaGift className="text-blue-600" />
                  ) : (
                    <FaMapMarkerAlt className="text-blue-600" />
                  )}
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
                    {serviceType === "rental" ? "Package" : "Route"}
                  </div>
                  <div className="font-medium text-gray-800">
                    {serviceType === "rental" ? (
                      <div>{packageName || "4 Hours/40 KM"}</div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span>{fromCity}</span>
                          <FaArrowRight className="text-xs text-blue-500" />
                          <span>{toCity}</span>
                        </div>
                        {viaCities.length > 0 && (
                          <div className="text-xs text-gray-600">
                            Via: {viaCities.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {serviceType === "rental" ? (
                    <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Local Rental Service
                    </div>
                  ) : (
                    distance && (
                      <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {Math.round(distance)} km{" "}
                        {viaCities.length > 0
                          ? `(${viaCities.length + 1} segments)`
                          : "journey"}
                      </div>
                    )
                  )}
                </div>
              </div>
              {/* Expandable button - only show when via cities exist */}
              {serviceType !== "rental" && viaCities.length > 0 && (
                <button
                  onClick={() => setIsRouteExpanded(!isRouteExpanded)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-blue-100 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-blue-200"
                  title="View detailed route"
                >
                  {isRouteExpanded ? (
                    <FaChevronUp size={14} />
                  ) : (
                    <FaChevronDown size={14} />
                  )}
                </button>
              )}
            </div>

            {/* Journey Type */}
            <div className="p-5 hover:bg-green-50 transition-colors duration-200">
              <div className="flex items-start">
                <div className="bg-green-100 p-3 rounded-full mr-4 shadow-sm">
                  <FaTaxi className="text-green-600" />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-green-600 mb-1">
                    {serviceType === "rental"
                      ? "City"
                      : serviceType === "airport"
                        ? "Local/Airport Transfer"
                        : journeyType === "outstation"
                        ? "Outstation"
                        : "Local"}
                  </div>
                  <div className="font-medium text-gray-800">
                    {serviceType === "rental"
                      ? fromCity
                      : serviceType === "airport"
                        ? "Airport Transfer"
                        : tripType === "roundTrip"
                        ? "Round Trip"
                        : viaCities.length > 0
                        ? "Multi-City Trip"
                        : "One Way Trip"}
                  </div>
                </div>
              </div>
            </div>

            {/* Departure Details */}
            <div className="p-5 hover:bg-purple-50 transition-colors duration-200 relative group">
              <div className="flex items-start">
                <div className="bg-purple-100 p-3 rounded-full mr-4 shadow-sm">
                  <FaCalendarAlt className="text-purple-600" />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-purple-600 mb-1">
                    Departure
                  </div>
                  <div className="font-medium text-gray-800">
                    {formatDateDDMMYYYY(dateStr)}
                  </div>
                  <div className="text-sm mt-2 flex items-center text-gray-600">
                    <FaClock className="text-purple-500 mr-1 text-xs" />{" "}
                    {timeStr}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowDateModal(true)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-purple-100 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-purple-200"
                title="Edit departure date and time"
              >
                <FaEdit size={14} />
              </button>
            </div>

            {/* Return Details */}
            <div className="p-5 hover:bg-yellow-50 transition-colors duration-200 relative group">
              <div className="flex items-start">
                <div
                  className={`p-3 rounded-full mr-4 shadow-sm ${tripType === "roundTrip" ? "bg-yellow-100" : "bg-gray-100"}`}
                >
                  <FaCalendarAlt
                    className={
                      tripType === "roundTrip"
                        ? "text-yellow-600"
                        : "text-gray-400"
                    }
                  />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-yellow-600 mb-1">
                    Return
                  </div>
                  {tripType === "roundTrip" ? (
                    <>
                      <div className="font-medium text-gray-800">
                        {formatDateDDMMYYYY(returnDate) !== "Not set"
                          ? formatDateDDMMYYYY(returnDate)
                          : "Not specified"}
                      </div>
                      {returnTime && (
                        <div className="text-sm mt-2 flex items-center text-gray-600">
                          <FaClock className="text-yellow-500 mr-1 text-xs" />{" "}
                          {returnTime}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-gray-400 font-medium">
                      No Return Trip
                    </div>
                  )}
                </div>
              </div>
              {tripType === "roundTrip" && (
                <button
                  onClick={() => setShowDateModal(true)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-yellow-100 text-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-yellow-200"
                  title="Edit return date and time"
                >
                  <FaEdit size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Route Details - Only show when expanded and via cities exist */}
      {isRouteExpanded && serviceType !== "rental" && viaCities.length > 0 && (
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 flex items-center">
                <FaRoute className="mr-2" />
                Full Route Path
              </h3>
              <p className="text-sm text-blue-600 mt-1">
                {Math.round(distance)} km • {viaCities.length + 1} segments
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Starting point */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <FaMapMarkerAlt className="text-green-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {fromCity}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    <div>{Math.round(distance / (viaCities.length + 1))} km</div>
                    <div>₹{Math.round((distance / (viaCities.length + 1)) * (baseRate || 12))}</div>
                  </div>
                </div>

                {/* Via cities */}
                {viaCities.map((viaCity, index) => (
                  <Fragment key={index}>
                    <div className="ml-3 flex items-center text-xs text-gray-500">
                      <FaArrowDown className="mr-2" />
                      <span>
                        {Math.round(distance / (viaCities.length + 1))} km • ₹{baseRate || 12}/km
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        <FaMapMarkerAlt className="text-blue-500" />
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {viaCity}
                          <span className="ml-2 text-xs text-blue-600">(Via)</span>
                        </div>
                      </div>
                      {index < viaCities.length && (
                        <div className="text-right text-xs text-gray-600">
                          <div>{Math.round(distance / (viaCities.length + 1))} km</div>
                          <div>₹{Math.round((distance / (viaCities.length + 1)) * (baseRate || 12))}</div>
                        </div>
                      )}
                    </div>
                  </Fragment>
                ))}

                {/* Final segment to destination */}
                <div className="ml-3 flex items-center text-xs text-gray-500">
                  <FaArrowDown className="mr-2" />
                  <span>
                    {Math.round(distance / (viaCities.length + 1))} km • ₹{baseRate || 12}/km
                  </span>
                </div>

                {/* Destination */}
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <FaMapMarkerAlt className="text-red-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {toCity}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-600">
                    <div>{Math.round(distance / (viaCities.length + 1))} km</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {cabOptions.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-yellow-800">
            No cabs available
          </h3>
          <p className="text-yellow-600 mt-2">
            Sorry, we couldn't find any cabs for your selected route and date.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Another Route
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* No cabs message */}
          {cabOptions.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-6 rounded-lg text-center">
              <h3 className="font-bold text-lg mb-2">No Cabs Available</h3>
              <p>
                Sorry, there are no cabs available for your selected route and
                date.
              </p>
              <p className="mt-4">
                Try changing your search criteria or contact customer support
                for assistance.
              </p>
            </div>
          )}



          {/* Cab listing header */}
          {cabOptions.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center mb-4">
                <FaTaxi className="text-blue-600 mr-2" />
                <h2 className="text-2xl font-bold">
                  Available Cabs ({cabOptions.length})
                </h2>
              </div>
              <p className="text-gray-600">
                Select the cab that best suits your needs for your journey
              </p>
            </div>
          )}

          {/* Cab list - Grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {safeCabOptions.map((cab) => {
              // Get price info including original and discounted price
              const priceInfo = getCabPrice(cab);

              return (
                <div
                  key={cab._id}
                  className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow ${selectedCab && selectedCab._id === cab._id ? "border-2 border-blue-500" : "border border-gray-200"}`}
                  onClick={() => handleSelectCab(cab)}
                >
                  {/* Mobile View */}
                  <div className="block md:hidden p-4 w-full">
                    {/* Price Section */}
                    <div className="mb-1">
                      <div className="flex items-center">
                        <div className="text-xl font-bold text-blue-600 flex items-center">
                          <FaRupeeSign className="text-sm mr-1" />
                          {priceInfo.originalPrice}
                        </div>
                      </div>
                    </div>

                    {/* Cab Type and Model */}
                    <div className="mb-1">
                      <div className="text-sm text-gray-600">
                        {(
                          cab.name ||
                          cab.category?.toUpperCase() ||
                          "SEDAN"
                        ).replace(/ \(AC\)/g, "")}{" "}
                        {cab.acType ? "(AC)" : ""}
                      </div>
                      <div className="font-semibold">
                        {cab.model ||
                          cab.description ||
                          "Swift, WagonR or Similar"}
                      </div>
                    </div>

                    {/* Journey Distance */}
                    <div className="mb-3">
                      <div className="flex justify-between">
                        <div className="font-medium">Included Km</div>
                        <div className="text-green-600">
                          {cab.includedKm} km
                        </div>
                      </div>
                    </div>

                    {/* Included Km */}
                    {cab.includedKm > 0 && (
                      <div className="mb-3">
                        <div className="flex justify-between">
                          <div className="font-medium">Included Km</div>
                          <div className="text-gray-700">
                            {cab.includedKm} Km
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Extra Fare per Km/Hour - Hide for Airport Transfer */}
                    {serviceType !== "airport" && (
                      <div className="mb-3">
                        <div className="flex justify-between">
                          <div className="font-medium">
                            {serviceType === "rental"
                              ? "Extra Fare/Hour"
                              : "Extra fare/Km"}
                          </div>
                          <div className="text-green-600">
                            ₹{" "}
                            {(() => {
                              // For rental services, get car-specific extra fare per hour from rental package
                              if (serviceType === "rental") {
                                const selectedPackage = safeRentalPackages.find(
                                  (pkg) => pkg && pkg.name === packageName,
                                );
                                if (selectedPackage) {
                                  console.log("Extra fare lookup for cab:", cab.name, "Package:", selectedPackage.name);

                                  // First check service cities for cab-specific extra fare per hour
                                  if (
                                    selectedPackage.serviceCities &&
                                    selectedPackage.serviceCities.length > 0
                                  ) {
                                    for (const serviceCity of selectedPackage.serviceCities) {
                                      console.log("Checking service city:", serviceCity.cityName, "extraFarePerHour:", serviceCity.extraFarePerHour);

                                      if (serviceCity.extraFarePerHour && cab.name) {
                                        // Try exact match first
                                        let extraFarePerHour = serviceCity.extraFarePerHour[cab.name];

                                        // If no exact match, try variations of the cab name
                                        if (!extraFarePerHour) {
                                          // Try with (AC) suffix
                                          const cabWithAC = `${cab.name} (AC)`;
                                          extraFarePerHour = serviceCity.extraFarePerHour[cabWithAC];
                                        }

                                        if (!extraFarePerHour) {
                                          // Try uppercase version
                                          const cabUpperCase = cab.name.toUpperCase();
                                          extraFarePerHour = serviceCity.extraFarePerHour[cabUpperCase];
                                        }

                                        if (!extraFarePerHour) {
                                          // Try uppercase with (AC)
                                          const cabUpperCaseAC = `${cab.name.toUpperCase()} (AC)`;
                                          extraFarePerHour = serviceCity.extraFarePerHour[cabUpperCaseAC];
                                        }

                                        console.log("Found extra fare for", cab.name, ":", extraFarePerHour);
                                        if (extraFarePerHour) {
                                          return extraFarePerHour;
                                        }
                                      }
                                    }
                                  }

                                  // Fallback to legacy cab type extra fare per hour
                                  if (
                                    selectedPackage.cabTypePrices &&
                                    selectedPackage.cabTypePrices.length > 0
                                  ) {
                                    const cabTypePricing =
                                      selectedPackage.cabTypePrices.find(
                                        (cp) =>
                                          (cab.name && cp.cabTypeId?.name === cab.name) ||
                                          cp.cabTypeId?._id === cab._id,
                                      );
                                    if (
                                      cabTypePricing &&
                                      cabTypePricing.extraFarePerHour
                                    ) {
                                      return cabTypePricing.extraFarePerHour;
                                    }
                                  }

                                  // Package-level extra fare per hour as fallback
                                  if (selectedPackage.extraFarePerHour) {
                                    return selectedPackage.extraFarePerHour;
                                  }
                                }
                                return 50; // Default extra fare per hour
                              }

                              // For outstation services, get correct rate based on cab name to match the calculation logic
                              if (cab.name && cab.name.toLowerCase().includes("tiago")) {
                                return 12;
                              } else if (
                                cab.name && cab.name.toLowerCase().includes("dsada")
                              ) {
                                return 14;
                              } else if (
                                cab.name && cab.name.toLowerCase().includes("adarsh")
                              ) {
                                return 10;
                              } else if (
                                cab.category?.toLowerCase().includes("luxury")
                              ) {
                                return 18;
                              } else if (
                                cab.category?.toLowerCase().includes("suv")
                              ) {
                                return 15;
                              } else {
                                return 10;
                              }
                            })()}
                            /{serviceType === "rental" ? "Hour" : "Km"}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Included Services */}
                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between">
                        <div className="font-medium">Fuel Charges</div>
                        <div className="text-green-600">Included</div>
                      </div>
                      <div className="flex justify-between">
                        <div className="font-medium">Driver Charges</div>
                        <div className="text-green-600">Included</div>
                      </div>
                      <div className="flex justify-between">
                        <div className="font-medium">Night Charges</div>
                        <div className="text-green-600">Included</div>
                      </div>
                    </div>

                    {/* Other Terms - Only shown if available */}
                    {cab.otherTerms && (
                      <div className="mb-3">
                        <div className="flex justify-between">
                          <div className="font-medium">Other Terms</div>
                          <div className="text-gray-700">{cab.otherTerms}</div>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end mt-3">
                      <button
                        onClick={(e) => handleCabBooking(e, cab)}
                        className="bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>

                  {/* Desktop View - Vertical Card Style */}
                  <div className="hidden md:block p-6 h-full flex flex-col">
                    {/* Distance/Package Display */}
                    <div className="bg-blue-50 rounded-lg p-3 mb-4 text-center">
                      {(() => {
                        const queryParams = new URLSearchParams(
                          location.search,
                        );
                        const service = queryParams.get("service");
                        const packageName = queryParams.get("package");
                        const airportCode = queryParams.get("airport");

                        if (service === "rental" && packageName) {
                          return (
                            <>
                              <div className="text-sm text-blue-800 font-medium">
                                Rental Package
                              </div>
                              <div className="text-2xl font-bold text-blue-600">
                                {packageName}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Local Service
                              </div>
                            </>
                          );
                        } else if (service === "airport" && airportCode) {
                          return (
                            <>
                              <div className="text-sm text-blue-800 font-medium">
                                Airport Transfer
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {fromCity} to {toCity}
                              </div>
                            </>
                          );
                        } else {
                          return (
                            <>
                              <div className="text-sm text-blue-800 font-medium">
                                Calculated Distance
                              </div>
                              <div className="text-2xl font-bold text-blue-600">
                                {Math.round(distance || 0)} km
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                From {fromCity} to {toCity}
                              </div>
                            </>
                          );
                        }
                      })()}
                    </div>

                    {/* Car Image */}
                    <div className="flex justify-center mb-4">
                      <img
                        src={
                          cab.imageUrl ||
                          "https://via.placeholder.com/400x250?text=Cab"
                        }
                        alt={cab.name}
                        className="h-40 w-auto object-contain rounded-lg"
                      />
                    </div>

                    {/* Price Section */}
                    <div className="text-center mb-2 bg-gray-50 rounded-lg p-3">
                      <div className="text-3xl font-bold text-blue-600 flex items-center justify-center">
                        <FaRupeeSign className="text-2xl mr-1" />
                        {priceInfo.originalPrice}
                      </div>
                    </div>

                    {/* Cab Type and Model */}
                    <div className="text-center mb-3 mt-2">
                      <h2 className="font-bold text-xl text-gray-800">
                        {cab?.model || cab?.name || "Standard Cab"}
                      </h2>

                      {cab?.description && (
                        <p className="text-gray-500 text-sm mt-1">
                          {cab.description}
                        </p>
                      )}
                    </div>

                    {/* Pricing and Included Services - Horizontal Layout */}
                    <div className="space-y-2 text-sm mb-4">
                      {/* Hide Extra Fare for Airport Transfer */}
                      {serviceType !== "airport" && (
                        <div className="flex justify-between border-b pb-1">
                          <div className="text-black font-medium">
                            {serviceType === "rental"
                              ? "Extra Fare/Hour"
                              : "Extra fare/Km"}
                          </div>
                          <div className="text-green-500 font-medium">
                            ₹
                            {(() => {
                              if (serviceType === "rental") {
                                const selectedPackage = safeRentalPackages.find(
                                  (pkg) => pkg && pkg.name === packageName,
                                );
                                if (selectedPackage) {
                                  // First check service cities for cab-specific extra fare per hour
                                  if (
                                    selectedPackage.serviceCities &&
                                    selectedPackage.serviceCities.length > 0
                                  ) {
                                    for (const serviceCity of selectedPackage.serviceCities) {
                                      if (serviceCity.extraFarePerHour && cab.name) {
                                        // Try exact match first
                                        let extraFarePerHour = serviceCity.extraFarePerHour[cab.name];

                                        // If no exact match, try variations of the cab name
                                        if (!extraFarePerHour) {
                                          // Try with (AC) suffix
                                          const cabWithAC = `${cab.name} (AC)`;
                                          extraFarePerHour = serviceCity.extraFarePerHour[cabWithAC];
                                        }

                                        if (!extraFarePerHour) {
                                          // Try uppercase version
                                          const cabUpperCase = cab.name.toUpperCase();
                                          extraFarePerHour = serviceCity.extraFarePerHour[cabUpperCase];
                                        }

                                        if (!extraFarePerHour) {
                                          // Try uppercase with (AC)
                                          const cabUpperCaseAC = `${cab.name.toUpperCase()} (AC)`;
                                          extraFarePerHour = serviceCity.extraFarePerHour[cabUpperCaseAC];
                                        }

                                        if (extraFarePerHour) {
                                          return extraFarePerHour;
                                        }
                                      }
                                    }
                                  }

                                  // Fallback to legacy cab type extra fare per hour
                                  if (
                                    selectedPackage.cabTypePrices &&
                                    selectedPackage.cabTypePrices.length > 0
                                  ) {
                                    const cabTypePricing =
                                      selectedPackage.cabTypePrices.find(
                                        (cp) =>
                                          (selectedCab.name && cp.cabTypeId?.name ===
                                            selectedCab.name) ||
                                          cp.cabTypeId?._id === selectedCab._id,
                                      );
                                    if (
                                      cabTypePricing &&
                                      cabTypePricing.extraFarePerHour
                                    ) {
                                      return cabTypePricing.extraFarePerHour;
                                    }
                                  }

                                  // Package-level extra fare per hour as fallback
                                  if (selectedPackage.extraFarePerHour) {
                                    return selectedPackage.extraFarePerHour;
                                  }
                                }
                                return 50; // Default extra fare per hour
                              }
                              return Math.round(priceInfo.extraKmRate) || "0";
                            })()}
                            /{serviceType === "rental" ? "Hour" : "Km"}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between border-b pb-1">
                        <div className="text-black font-medium">
                          Fuel Charges
                        </div>
                        <div className="text-green-500 font-medium">
                          Included
                        </div>
                      </div>

                      <div className="flex justify-between border-b pb-1">
                        <div className="text-black font-medium">
                          Driver Charges
                        </div>
                        <div className="text-green-500 font-medium">
                          Included
                        </div>
                      </div>

                      <div className="flex justify-between border-b pb-1">
                        <div className="text-black font-medium">
                          Night Charges
                        </div>
                        <div className="text-green-500 font-medium">
                          Included
                        </div>
                      </div>
                    </div>

                    {cab.otherTerms && (
                      <div className="mb-4">
                        <div className="flex justify-between">
                          <div className="font-medium">Other Terms</div>
                          <div className="text-gray-700">{cab.otherTerms}</div>
                        </div>
                      </div>
                    )}

                    {/* Round Trip section removed as requested */}

                    {/* Book Now Button */}
                    <div className="mt-auto pt-4">
                      <button
                        onClick={(e) => handleCabBooking(e, cab)}
                        className="bg-blue-600 text-white py-3 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors w-full flex items-center justify-center"
                      >
                        <span className="mr-2">Book Now</span>
                        <FaArrowRight />
                      </button>
                    </div>

                    {/* Cab Location - Removed as requested */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Display error if any */}
      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded my-4"
          role="alert"
        >
          <p>{error}</p>
        </div>
      )}

      {/* Trip summary has been moved to the top header */}

      {selectedCab && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-md border-t p-4 z-10">
          <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center mb-4 sm:mb-0">
              <div>
                <h3 className="font-bold text-lg">{selectedCab.name}</h3>
                <p className="text-gray-600">
                  {serviceType === "rental"
                    ? `Local Rental in ${fromCity}`
                    : `${fromCity} To ${toCity}`}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="mr-6">
                <p className="text-gray-600">Total Fare</p>
                <p className="text-2xl font-bold text-blue-600 flex items-center">
                  <FaRupeeSign className="text-xl mr-1" />
                  {getCabPrice(selectedCab).originalPrice}
                </p>
                <p className="text-xs text-gray-500">
                  {serviceType === "rental"
                    ? `For ${packageName || "4 Hours/40 KM"} package`
                    : `For ${Math.round(distance || 0)} km journey`}
                </p>
              </div>
              <button
                onClick={handleBookNow}
                className="bg-blue-600 text-white py-3 px-8 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarSelection;
